import type {QueryClient} from '@tanstack/react-query';

import {getPageTypeFromUrl} from 'coreUtils/url';
import {PageType} from 'coreUtils/IUtils';

import {CURRENT_ROUTE_KEY, type DeferredRoute} from './useCurrentRoute';
import {resolveDeferred} from './useDeferredData';
import {SEO_DATA_KEY} from './useSeo';

interface ResolvePageDeferredsOptions {
	/** When true, skip keys already present in the query cache (safety-net mode). */
	onlyIfMissing?: boolean;
}

/**
 * Builds the deferred route object consumed by `useDeferredRoute()`.
 */
const buildDeferredRoute = (currentRoute: string, isHomePage = false): DeferredRoute => {
	const pageType = getPageTypeFromUrl(currentRoute) as PageType | undefined;

	return {
		currentRoute,
		isHomePage,
		pageType,
		isPDP: pageType === PageType.PDP,
		isPLP: pageType === PageType.PLP,
		isSplashPage: pageType === PageType.SplashPage,
		is404: pageType === PageType.NOT_FOUND,
		isEditorial: pageType === PageType.Editorial,
		isMyAccount: pageType === PageType.MY_ACCOUNT,
	};
};

/**
 * Resolves the `current-route` and `seo-data` deferred promises so shell
 * components (`SuspenseSEO`, Header, Footer) stop suspending.
 */
const resolvePageDeferreds = (
	qc: QueryClient,
	currentRoute: string,
	isHomePage = false,
	options: ResolvePageDeferredsOptions = {},
): void => {
	const {onlyIfMissing = false} = options;
	const routeQueryKey = [CURRENT_ROUTE_KEY];
	const seoQueryKey = [SEO_DATA_KEY];

	if (!onlyIfMissing || qc.getQueryData(routeQueryKey) == null) {
		resolveDeferred(qc, CURRENT_ROUTE_KEY, buildDeferredRoute(currentRoute, isHomePage));
	}

	if (!onlyIfMissing || qc.getQueryData(seoQueryKey) == null) {
		resolveDeferred(qc, SEO_DATA_KEY, true);
	}
};

export {buildDeferredRoute, resolvePageDeferreds};
export type {ResolvePageDeferredsOptions};
